import { Link } from "@tanstack/react-router";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import type { ShopifyProduct } from "@/lib/shopify";
import { formatMoney, cardPriceFromProduct } from "@/lib/shopify";
import { isJustArrived } from "@/lib/freshness";
import { useWishlist } from "@/hooks/useWishlist";
import { useT } from "@/lib/i18n";
import { shopifyImg, shopifySrcSet } from "@/lib/shopify-image";
import { trackProductClick } from "@/lib/analytics";

interface Props {
  product: ShopifyProduct;
  className?: string;
  listId?: string;
  listName?: string;
  index?: number;
}

export function ProductCard({ product, className, listId, listName, index }: Props) {
  const node = product.node;
  const img = node.images.edges[0]?.node;
  const hover = node.images.edges[1]?.node;

  const fresh = isJustArrived(node);
  const { has, toggle } = useWishlist();
  const { t } = useT();
  const wished = has(node.handle);
  const { price: displayPrice, compareAt, isRange, isOnSale } = cardPriceFromProduct(node);

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const adding = !wished;
    toggle(node.handle);
    toast(adding ? t("wishlist.toastAdded") : t("wishlist.toastRemoved"), {
      description: node.title,
      action: adding
        ? {
            label: t("wishlist.toastAction"),
            onClick: () => {
              if (typeof window !== "undefined") window.location.href = "/onskeliste";
            },
          }
        : undefined,
    });
  };

  return (
    <Link
      to="/products/$handle"
      params={{ handle: node.handle }}
      onClick={() => {
        // Remember which collection the user was browsing so the product
        // page can show a "back to <collection>" link.
        if (typeof window !== "undefined" && listId?.startsWith("collection:")) {
          try {
            sessionStorage.setItem(
              "fl:lastCollection",
              JSON.stringify({
                handle: listId.replace(/^collection:/, ""),
                name: listName ?? "",
              }),
            );
          } catch {}
        }
        trackProductClick({
          id: node.id,
          handle: node.handle,
          title: node.title,
          price: node.priceRange.minVariantPrice,
          list_id: listId,
          list_name: listName,
          index,
        });
      }}
      className={`group block ${className ?? ""}`}
    >
      <div className="lamp-glow-on-hover relative aspect-[4/5] overflow-hidden rounded-sm bg-muted transition-shadow duration-700">
        {img && (
          <img
            src={shopifyImg(img.url, { width: 480 })}
            srcSet={shopifySrcSet(img.url, [320, 480, 640, 800])}
            sizes="(min-width: 1024px) 320px, (min-width: 640px) 45vw, 90vw"
            alt={img.altText ?? node.title}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
          />
        )}
        {hover && (
          <img
            src={shopifyImg(hover.url, { width: 480 })}
            srcSet={shopifySrcSet(hover.url, [320, 480, 640, 800])}
            sizes="(min-width: 1024px) 320px, (min-width: 640px) 45vw, 90vw"
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 group-hover:opacity-100"
          />
        )}

        {/* Badges (top-left): Solgt > Tilbud > Nyt */}
        {!node.availableForSale ? (
          <span className="absolute left-3 top-3 rounded-full bg-foreground/85 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-background">
            {t("badge.sold")}
          </span>
        ) : isOnSale ? (
          <span className="absolute left-3 top-3 rounded-full bg-terracotta px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-primary-foreground shadow-sm">
            {t("badge.sale")}
          </span>
        ) : fresh ? (
          <span className="absolute left-3 top-3 rounded-full bg-terracotta px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-primary-foreground shadow-sm">
            {t("badge.new")}
          </span>
        ) : null}

        {/* Wishlist heart (top-right) — always visible so it's tappable on touch devices */}
        <button
          type="button"
          onClick={handleWishlistClick}
          aria-label={wished ? t("wishlist.remove") : t("wishlist.add")}
          aria-pressed={wished}
          className={`absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-background/85 backdrop-blur-sm transition-all hover:scale-110 hover:bg-background ${
            wished ? "text-terracotta" : "text-foreground/70"
          } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta`}
        >
          <Heart
            className="h-4 w-4 transition-colors"
            fill={wished ? "currentColor" : "none"}
            strokeWidth={1.6}
          />
        </button>
      </div>
      <div className="mt-4">
        <div className="mt-1.5 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <h3 className="font-display text-[15px] sm:text-[17px] font-normal leading-snug text-foreground line-clamp-2 transition-colors group-hover:text-terracotta">
            {node.title}
          </h3>
          <span className="flex flex-wrap items-baseline gap-x-2 whitespace-nowrap pt-0 sm:pt-0.5 text-sm">
            <span className={isOnSale ? "text-terracotta font-medium" : "text-foreground/85"}>
              {isRange ? t("price.from", { price: formatMoney(displayPrice) }) : formatMoney(displayPrice)}
            </span>
            {isOnSale && compareAt && (
              <span
                className="text-xs text-muted-foreground line-through"
                aria-label={t("price.was", { price: formatMoney(compareAt) })}
              >
                {formatMoney(compareAt)}
              </span>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
